import React from 'react';
import {
  Action,
  ActionIcon,
  Card, DetailStack, FormHandlers, Grid, ICON_CLOSE, ICON_COMPARE_OUTLINE, ICON_PLUS, Portal,
  useModal,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { PropertyUnit } from '../../../providers/api/dtos';
import { energyClassLabels, propertyUnitIntendedUseLabels } from '../../../labels';
import { NO_VALUE_SYMBOL } from '../../utils';
import { removeNullAndUndefinedValues } from '../../../utils/array';
import { SuggestedPropertyUnit } from '../../hooks/use-ai-suggested-cadastral-registry/useAiSuggestedCadastralRegistry';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import PropertyUnitForm, { PropertyUnitFormModel } from '../property-unit-form/PropertyUnitForm';
import { toPropertyUnit, toPropertyUnitFormModel } from '../property-unit-form/mappers';

const highlightPropertyUnitField = (suggestedPropertyUnit: SuggestedPropertyUnit, field: keyof PropertyUnit) => (
  suggestedPropertyUnit.status === 'partial_match' && suggestedPropertyUnit.suggestion[field] !== suggestedPropertyUnit.existing?.[field]
    ? 'critical.default.low'
    : undefined
);

export interface SuggestedPropertyUnitCardProps extends React.AriaAttributes {
  suggestedPropertyUnit: SuggestedPropertyUnit,
  onAdd?: (addedPropertyUnit: PropertyUnit) => Promise<void> | void,
  onCompare?: (suggestedPropertyUnit: PropertyUnit, existingPropertyUnit: PropertyUnit) => Promise<void> | void,
  onIgnore?: (ignoredPropertyUnit: PropertyUnit) => Promise<void> | void,
}

const SuggestedPropertyUnitCard: React.FC<SuggestedPropertyUnitCardProps> = ({
  suggestedPropertyUnit,
  onAdd,
  onCompare,
  onIgnore,
  ...rest
}) => {
  const modal = useModal();
  const formRef = React.useRef<FormHandlers>() as React.MutableRefObject<FormHandlers>;
  const [isSaving, setIsSaving] = React.useState(false);

  const handleAdd = React.useCallback(async (formValues: PropertyUnitFormModel) => {
    if (suggestedPropertyUnit.status !== 'new') return;

    setIsSaving(true);

    const addedPropertyUnit = toPropertyUnit(formValues);

    await onAdd?.(addedPropertyUnit);

    setIsSaving(false);
    modal.close();
  }, [suggestedPropertyUnit.status, onAdd, modal]);

  const handleCompare = React.useCallback(async (formValues: PropertyUnitFormModel) => {
    if (suggestedPropertyUnit.status !== 'partial_match') return;

    setIsSaving(true);

    const updatedPropertyUnit = toPropertyUnit(formValues);

    await onCompare?.(updatedPropertyUnit, suggestedPropertyUnit.existing);

    setIsSaving(false);
    modal.close();
  }, [onCompare, suggestedPropertyUnit, modal]);

  return (
    <>
      <Card aria-label="Card di una unità immobiliare suggerita" {...rest}>
        <Card.Header
          title="Unità immobiliare"
          primaryActions={removeNullAndUndefinedValues([
            suggestedPropertyUnit.status === 'new' && (
              <ActionIcon
                icon={{ path: ICON_PLUS }}
                label="Aggiungi"
                aria-label="Aggiungi unità immobiliare suggerita"
                onClick={() => modal.open()}
                size="S"
              />
            ),
            suggestedPropertyUnit.status === 'partial_match' && (
              <ActionIcon
                icon={{ path: ICON_COMPARE_OUTLINE }}
                label="Compara/Aggiorna"
                aria-label="Compara e aggiorna unità immobiliare suggerita"
                onClick={() => modal.open()}
                size="S"
              />
            ),
            <ActionIcon
              label="Ignora"
              aria-label="Ignora unità immobiliare suggerita"
              onClick={() => onIgnore?.(suggestedPropertyUnit.suggestion)}
              icon={{ path: ICON_CLOSE }}
              size="S"
            />,
          ])}
        />
        <Card.Content>
          <Grid gutter={150}>
            <Grid.Unit size={1 / 4}>
              <DetailStack
                label="Sezione"
                color={highlightPropertyUnitField(suggestedPropertyUnit, 'section')}
              >
                {suggestedPropertyUnit.suggestion.section || NO_VALUE_SYMBOL}
              </DetailStack>
            </Grid.Unit>

            <Grid.Unit size={1 / 4}>
              <DetailStack
                label="Foglio"
                color={highlightPropertyUnitField(suggestedPropertyUnit, 'sheet')}
              >
                {suggestedPropertyUnit.suggestion.sheet || NO_VALUE_SYMBOL}
              </DetailStack>
            </Grid.Unit>

            <Grid.Unit size={1 / 4}>
              <DetailStack
                label="Particella"
                color={highlightPropertyUnitField(suggestedPropertyUnit, 'parcel')}
              >
                {suggestedPropertyUnit.suggestion.parcel || NO_VALUE_SYMBOL}
              </DetailStack>
            </Grid.Unit>

            <Grid.Unit size={1 / 4}>
              <DetailStack
                label="Subalterno"
                color={highlightPropertyUnitField(suggestedPropertyUnit, 'subunit')}
              >
                {suggestedPropertyUnit.suggestion.subunit || NO_VALUE_SYMBOL}
              </DetailStack>
            </Grid.Unit>

            <Grid.Unit size={1 / 4}>
              <DetailStack
                label="Categoria"
                color={highlightPropertyUnitField(suggestedPropertyUnit, 'category')}
              >
                {suggestedPropertyUnit.suggestion.category || NO_VALUE_SYMBOL}
              </DetailStack>
            </Grid.Unit>

            <Grid.Unit size={2 / 4}>
              <DetailStack
                label="Destinazione d'uso"
                color={highlightPropertyUnitField(suggestedPropertyUnit, 'intendedUse')}
              >
                {suggestedPropertyUnit.suggestion.intendedUse ? propertyUnitIntendedUseLabels[suggestedPropertyUnit.suggestion.intendedUse] : NO_VALUE_SYMBOL}
              </DetailStack>
            </Grid.Unit>

            <Grid.Unit size={1 / 4}>
              <DetailStack
                label="Rendita"
                color={highlightPropertyUnitField(suggestedPropertyUnit, 'value')}
              >
                {suggestedPropertyUnit.suggestion.value ? formatEuro(suggestedPropertyUnit.suggestion.value) : NO_VALUE_SYMBOL}
              </DetailStack>
            </Grid.Unit>

            <Grid.Unit>
              <DetailStack
                label="Classe energetica"
                color={highlightPropertyUnitField(suggestedPropertyUnit, 'energyClass')}
              >
                {suggestedPropertyUnit.suggestion.energyClass ? energyClassLabels[suggestedPropertyUnit.suggestion.energyClass] : NO_VALUE_SYMBOL}
              </DetailStack>
            </Grid.Unit>
          </Grid>
        </Card.Content>
      </Card>

      <Portal>
        <SimpleModal
          {...modal}
          title={
            suggestedPropertyUnit.status === 'partial_match'
              ? 'Compara e aggiorna unità immobiliare'
              : 'Aggiungi unità immobiliare'
          }
          aria-label={
            suggestedPropertyUnit.status === 'partial_match'
              ? "Modale per comparare e aggiornare l'unità immobiliare"
              : "Modale per aggiungere un'unità immobiliare"
          }
          footer={(
            <Action
              label={
                suggestedPropertyUnit.status === 'partial_match'
                  ? 'Aggiorna'
                  : 'Aggiungi'
              }
              color="primary"
              emphasis="high"
              onClick={() => formRef.current?.handleSubmit()}
              loading={isSaving}
            />
          )}
        >
          <PropertyUnitForm
            onSubmit={suggestedPropertyUnit.status === 'partial_match' ? handleCompare : handleAdd}
            initialValues={suggestedPropertyUnit.suggestion ? toPropertyUnitFormModel(suggestedPropertyUnit.suggestion) : {}}
            oldValues={suggestedPropertyUnit.status === 'partial_match' ? toPropertyUnitFormModel(suggestedPropertyUnit.existing) : {}}
            innerRef={formRef}
            hideSubmit
            loading={isSaving}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default SuggestedPropertyUnitCard;
